import type {
  SendConfigurationToShoppingCartRequest,
  SendConfigurationToShoppingCartResponse,
  AddItemsToShoppingCartRequest,
  AddItemsToShoppingCartResponse,
  ArticleDetailRequest,
  ArticlePriceResponse,
  ShoppingCartConfigurationIdResponse,
  ArticleConfigTotalPriceAndDeliveryTimesRequest,
  ArticleConfigTotalPriceAndDeliveryTimesResponse,
  ShoppingCartPositionResponse,
  ArticleDetailResponse,
} from '@models/api/shopping-cart.types';
import { ConfigurationType } from '@models/api/shopping-cart.types';
import { serviceApiRequest } from '@libs/service-api-request';
import type { ContactPerson, TeaserContent, TeaserContentFail } from '@models/graphql';
import { DEFAULT_DISTRICT_CODE_FR, DEFAULT_ZIP_CODE_DE, SESSION_KEEP_ALIVE_TIMEOUT } from '@libs/constants';
import type { AddItemToWishlistRequest } from '@models/api/wishlist.types';
import type { NodeContact } from '@models/api/node-contact.types';
import type { Footer, LeadIn, PageContent } from '@models/api/page-content.types';
import type { RcSearchByManufacturerArticleNoResponse } from '@models/api/rc-search-by-maufacturer-article.types';
import type { CountryNotification } from '@models/api/country-notification.types';
import type { Session } from '@hooks/states/useSessionStore';
import { useSessionStore } from '@hooks/states/useSessionStore';
import type { SeoContent } from '@models/api/seo-content.types';
import type { ContactAreaType } from '@models/api/contact-area-type.types';
import { Logger } from '@icommerce/logger';

export type LocaleParams = {
  country?: string;
  language?: string;
};

/**
 * Fetch autocomplete proposals from the igus product search.
 * @param searchTerm
 * @param serviceRequest
 */
let keepAliveTID: NodeJS.Timeout | undefined;

// This is used to keep the MMA client session alive and update the interval id
// after session refresh. It is recommended to call this function every time
// you do changes in the MMA session, f.e. shopping cart updates or language change.
export const updateSessionStore = async (locale: LocaleParams = {}, serviceRequest = serviceApiRequest) => {
  const { setSession } = useSessionStore.getState();
  // Insure we pass only needed params from the locale object
  const { country, language } = locale;

  const session = await serviceRequest<Promise<Session>>(`/shop-session`, false, { country, language });
  setSession(session);

  // Set / reset the keep alive timer after session storage update
  if (keepAliveTID) {
    clearInterval(keepAliveTID);
  }
  keepAliveTID = setInterval(updateSessionStore, SESSION_KEEP_ALIVE_TIMEOUT);
};

export async function productSearchAutocomplete(searchTerm: string, serviceRequest = serviceApiRequest) {
  return serviceRequest<string[]>('/search/autocomplete', false, { term: searchTerm });
}

export async function getPublicShopSession(params: LocaleParams = {}, serviceRequest = serviceApiRequest) {
  return serviceRequest<Session>('/shop-session', true, params);
}

export async function shoppingCartAddItems(body: AddItemsToShoppingCartRequest, serviceRequest = serviceApiRequest) {
  return serviceRequest<AddItemsToShoppingCartResponse>('/shopping-cart', false, undefined, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}

export async function getShoppingCartConfigurationId(serviceRequest = serviceApiRequest) {
  return serviceRequest<ShoppingCartConfigurationIdResponse>('/shopping-cart/configuration-id', false, undefined, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ configurationType: ConfigurationType.ECC }),
  });
}

export async function shoppingCartAddConfiguration<DataT>(
  body: SendConfigurationToShoppingCartRequest<DataT>,
  serviceRequest = serviceApiRequest
) {
  return serviceRequest<SendConfigurationToShoppingCartResponse>('/shopping-cart/configuration', false, undefined, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}

export async function shoppingCartUpdateConfiguration<DataT>(
  body: SendConfigurationToShoppingCartRequest<DataT>,
  serviceRequest = serviceApiRequest
) {
  if (!body.cartPositionId) {
    throw new Error('cartPositionId is not set in request body');
  }
  return serviceRequest<SendConfigurationToShoppingCartResponse>('/shopping-cart/configuration', false, undefined, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}

export async function shoppingCartPosition(cartPositionId: string, serviceRequest = serviceApiRequest) {
  return serviceRequest<ShoppingCartPositionResponse>(
    `/shopping-cart/position?cartPositionId=${cartPositionId}`,
    false,
    undefined,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
}

// #region getArticleDetail
/**
 * Fetch details for articles.
 *
 * The service API has the two response modes 'text' and 'number'
 * We can use overloads to handle both of the responses modes
 */
export async function getArticleDetail(
  body: ArticleDetailRequest & { textMode: true },
  serviceRequest?: typeof serviceApiRequest
): Promise<ArticleDetailResponse<'text'>>;

export async function getArticleDetail(
  body: ArticleDetailRequest & { textMode: false },
  serviceRequest?: typeof serviceApiRequest
): Promise<ArticleDetailResponse<'number'>>;

export async function getArticleDetail(
  body: ArticleDetailRequest,
  serviceRequest = serviceApiRequest
): Promise<ArticleDetailResponse<'text' | 'number'>> {
  const mode = body.textMode ? 'text' : 'number';
  return serviceRequest<ArticleDetailResponse<typeof mode>>('/article/detail', false, undefined, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}
// #endregion

/**
 * Fetch prices for articles.
 */
export async function getArticlePrice(
  body: ArticleDetailRequest,
  serviceRequest = serviceApiRequest
): Promise<ArticlePriceResponse> {
  return serviceRequest<ArticlePriceResponse>('/article/price', false, undefined, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}

/**
 * Fetch total price and delivery times for articles or article configurations.
 */
export async function getArticleConfigTotalPriceAndDeliveryTimes(
  body: ArticleConfigTotalPriceAndDeliveryTimesRequest,
  serviceRequest = serviceApiRequest
): Promise<ArticleConfigTotalPriceAndDeliveryTimesResponse> {
  return serviceRequest<ArticleConfigTotalPriceAndDeliveryTimesResponse>(
    '/article/total-price-and-delivery-times',
    false,
    undefined,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }
  );
}

/**
 * Get contact Persons based on Postal Code
 * @param zipCode
 * @param serviceRequest
 * @return ContactPerson[]
 */
export async function getContactPersonsByZipCode(
  zipCode: string = DEFAULT_ZIP_CODE_DE,
  currentDomain = 'https://www.igus.de',
  serviceRequest = serviceApiRequest
) {
  return serviceRequest<ContactPerson[]>(`/search/contact-persons`, false, {
    zipCode,
    currentDomainLang: currentDomain,
  });
}

/**
 * Get contact Persons based on District Code
 * @param districtCode
 * @param serviceRequest
 * @return ContactPerson[]
 */
export async function getContactPersonsByDistrictCode(
  districtCode: string = DEFAULT_DISTRICT_CODE_FR,
  currentDomain = 'https://www.igus.de',
  serviceRequest = serviceApiRequest
) {
  return serviceRequest<ContactPerson[]>(`/search/contact-persons`, false, {
    districtCode,
    currentDomainLang: currentDomain,
  });
}

/**
 * @param locale
 */
export async function getContactAreaType(locale: string, serviceRequest = serviceApiRequest) {
  return serviceRequest<ContactAreaType>(`/search/contact-area-type/${locale}`, true);
}

/**
 * Get content from SPCK for a list of teaser ids.
 * @param teaserIds      A list of teaser ids to request
 * @param locale         The locale, for example "de-DE"
 * @param serviceRequest The http request callback function
 */
export async function getTeaserContents(
  teaserIds: string[],
  locale: string,
  returnData = true,
  isPublicRequest = true,
  serviceRequest = serviceApiRequest
): Promise<TeaserContent[] | boolean> {
  const requestPromises = teaserIds.map(teaserId =>
    serviceRequest<TeaserContent>(
      `/teaser-content/${locale}/${teaserId}?returnData=${String(returnData)}`,
      isPublicRequest
    )
  );

  const teasers = await Promise.all(
    requestPromises.map(p =>
      p.catch((error: { path: string }) => {
        Logger.warn({ message: `Teaser could not be found`, error }, 'UnableToGetTeaser');
        return null;
      })
    )
  );

  if (!returnData) {
    return teasers.some(teaser => (teaser as unknown as { exists: boolean })?.exists === true);
  }
  return teasers.filter(
    teaser => teaser !== null && !(teaser as unknown as TeaserContentFail).errorCode
  ) as TeaserContent[];
}

/**
 * Fetch data for the country notification based on the locale.
 * This API endpoint is typically used to retrieve DeliveryLocationBanner information.
 * @param locale          the locale - e.g. de-DE
 * @param serviceRequest  the http function
 */
export async function getCountryNotification(locale: string, serviceRequest = serviceApiRequest) {
  return serviceRequest<CountryNotification>(`/banner-content/${locale}/ip-country-notification`, true);
}

/**
 * Fetch contact data based on given node and locale.
 * This API endpoint is typically used to retrieve ContactBox information.
 * @param nodeId          a contact is attached to a tree node in CMS
 * @param locale          the locale - e.g. de-DE
 * @param serviceRequest  the http function
 */
export async function getNodeContact(nodeId: number, locale: string, serviceRequest = serviceApiRequest) {
  return serviceRequest<NodeContact>(`/contact-person/node/${locale}/${nodeId}`, true);
}

/**
 * Fetch page content for category pages based on given node and locale.
 * @param nodeId          a contact is attached to a tree node in CMS
 * @param locale          the locale - e.g. de-DE
 * @param serviceRequest  the http function
 */
export async function getPageContent(nodeId: number, locale: string, serviceRequest = serviceApiRequest) {
  return serviceRequest<PageContent>(`/page-content/node/${locale}/${nodeId}`, true);
}

/**
 * Fetch seo content for category pages based on given node and locale.
 * @param nodeId          a contact is attached to a tree node in CMS
 * @param locale          the locale - e.g. de-DE
 * @param serviceRequest  the http function
 */
export async function getSeoContent(nodeId: number, locale: string, serviceRequest = serviceApiRequest) {
  return serviceRequest<SeoContent>(`/page-content/seo/${locale}/${nodeId}`, true);
}

/**
 * Add item to Wishlist
 * @param body
 * @param serviceRequest
 */
export async function wishListAddItem(body: AddItemToWishlistRequest, serviceRequest = serviceApiRequest) {
  return serviceRequest('/wishlist', false, undefined, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}

/**
 * Search articles by manufacturer article No
 * @param manufacturerArticleNo   the manufacturer article No
 * @param manufacturerId  the manufacturer id
 * @param serviceRequest
 */
export async function readycableSearchByManufacturerArticleNo(
  manufacturerArticleNo: string,
  manufacturerId = 0,
  serviceRequest = serviceApiRequest
) {
  return serviceRequest<RcSearchByManufacturerArticleNoResponse>('/search/readycable/manufactorer-art-nr', true, {
    manufacturerArticleNo,
    manufacturerId,
  });
}

/**
 * Retrieves the json data for the footer by language
 * @param locale   the current locale
 * @param serviceRequest
 */
export async function getFooterByLocale(locale: string, serviceRequest = serviceApiRequest) {
  return serviceRequest<Footer>(`/page-content/parts/footer/${locale}`, true);
}

/**
 * Retrieves the json data for the leadin by language
 * @param locale   the current locale
 * @param serviceRequest
 */
export async function getLeadInByLocale(locale: string, serviceRequest = serviceApiRequest) {
  return serviceRequest<LeadIn>(`/page-content/parts/leadin/${locale}`, true);
}

/**
 * Retrieves the json data for the chainflex overstock sale items
 * @param searchTerm the search term for the overstock sale
 * @param locale the current locale
 * @param serviceRequest
 */
export async function getChainflexOverstockItems(
  searchTerm: string,
  locale: string,
  serviceRequest = serviceApiRequest
) {
  return serviceRequest<string>(`/overstock-sale/chainflex?searchTerm=${searchTerm}&locale=${locale}`, false);
}

/**
 * Adds an overstock item to the shopping cart
 * @param locale the current locale
 * @param articleNo the sku of the item
 * @param storageLocation the storageLocation for the overstock sale shopping cart functionality
 * @param chainflexMeterLength the length of the overstock item
 * @param serviceRequest
 */
export async function addChainflexOverstockItemToShoppingCart(
  locale: string,
  articleNo: string,
  storageLocation: string,
  chainflexMeterLength: number,
  serviceRequest = serviceApiRequest
) {
  return serviceRequest<Response>(
    `/overstock-sale/shopping-cart?locale=${locale}&articleNo=${articleNo}&storageLocation=${storageLocation}&chainflexMeterLength=${chainflexMeterLength}`,
    false,
    undefined,
    { method: 'POST', credentials: 'include' }
  );
}
